import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Copy, Check, ExternalLink, RefreshCw, Send, Globe } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";

// ============================================================================
// PortalCommsCard (PORTAL-1; COMMS-UX-01 rework 2026-10-09) — the customer
// portal card on the 360 Messages tab, now about ONE thing: the portal.
//
// Seth: "The customer portal link is odd and not friendly or pleasing to the
// eye on what its there for." It now leads with what the portal IS, then the
// actions. The old consent footer is GONE — it read the legacy
// email_suppression table (empty → rendered "Marketing email · Opted in"
// under a do-not-contact customer). Contact truth lives in the Reach card
// above; this card never speaks about consent again.
// ============================================================================

export function PortalCommsCard({ custno }: { custno: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["portal_comms", custno],
    queryFn: async () => {
      const [tok, setting, loc] = await Promise.all([
        supabase.rpc("get_customer_link_token", { p_custno: custno }),
        supabase.from("org_setting").select("value").eq("key", "public_app_url").maybeSingle(),
        supabase.from("mirror_location").select("email, email2").eq("custno", custno).order("locno").limit(10),
      ]);
      const base = (typeof setting.data?.value === "string" ? setting.data.value : "https://my.sedgwickheating.com").replace(/\/+$/, "");
      // Every distinct on-file address (location email + email2 across locations).
      const emails = [
        ...new Set(
          ((loc.data ?? []) as { email: string | null; email2: string | null }[])
            .flatMap((r) => [r.email, r.email2])
            .map((e) => (e ?? "").trim().toLowerCase())
            .filter((e) => e.includes("@")),
        ),
      ];
      return { link: `${base}/portal?t=${tok.data ?? ""}`, emails };
    },
  });
  // 360-AUDIT wave 2 (C): resetting/sending links are account actions —
  // grantable on their own key at rollout (view stays for everyone).
  const canPortal = useCan("portal.manage");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [linkSent, setLinkSent] = useState<{ ok: boolean; msg: string } | null>(null);

  function copy() {
    if (!data) return;
    navigator.clipboard?.writeText(data.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  async function resetLink() {
    if (!window.confirm("Reset this customer's portal link?\n\nTheir current link stops working immediately. Use this after an ownership change, or if a link was shared by mistake.")) return;
    setBusy(true);
    const { error } = await supabase.rpc("rotate_customer_link_token", { p_custno: custno });
    setLinkSent(error
      ? { ok: false, msg: "Couldn't reset the link — the OLD link still works. Try again." }
      : { ok: true, msg: "Link reset — the old link is dead; copy or email the new one." });
    await qc.invalidateQueries({ queryKey: ["portal_comms", custno] });
    setBusy(false);
  }
  async function sendTo(emails: string[]) {
    if (!data || emails.length === 0) return;
    setBusy(true);
    setLinkSent(null);
    const { data: sd, error } = await supabase.functions.invoke("send-email", {
      body: { to: emails, subject: "Your Sedgwick Heating account", html: portalLinkEmail(data.link), custno, message_type: "portal-link" },
    });
    const r = sd as { ok?: boolean; error?: string; blocked?: string } | null;
    setBusy(false);
    if (r?.blocked === "testing" || /testing|paused/i.test(r?.error ?? "")) {
      setLinkSent({ ok: true, msg: "Comms are in testing — nothing sent yet. Goes live at rollout." });
      setEmailing(false);
    } else if (error || !r?.ok) {
      setLinkSent({ ok: false, msg: r?.error === "secrets_missing" ? "Email isn't switched on yet." : "Couldn't send — try again." });
    } else {
      setLinkSent({ ok: true, msg: `Sent to ${emails.length === 1 ? emails[0] : `${emails.length} people`}` });
      setEmailing(false);
    }
  }

  if (!data) return null;

  return (
    <div className="rounded-xl border border-hairline bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
          <Globe className="h-4.5 w-4.5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">Customer Portal</h3>
          <p className="mt-0.5 text-xs leading-snug text-slate-500">
            Their self-service page — equipment &amp; warranties, invoices, membership, and photo uploads,
            no password needed. The link is their private sign-in: anyone holding it sees this account,
            so share it only with the customer.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2">
        <span className="mr-auto truncate text-xs font-medium text-slate-500">my.sedgwickheating.com · private link</span>
        <button onClick={copy} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy link"}
        </button>
        {canPortal && (
          <button
            onClick={() => { setEmailing((o) => !o); setLinkSent(null); }}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors",
              emailing ? "border-brand-300 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            <Send className="h-3.5 w-3.5" /> Email it
          </button>
        )}
        <a href={data.link} target="_blank" rel="noreferrer" title="Open the portal as the customer sees it" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-500 transition-colors hover:bg-slate-50">
          <ExternalLink className="h-3.5 w-3.5" /> Open
        </a>
        {canPortal && (
          <button onClick={resetLink} disabled={busy} title="Reset the link — the old one stops working (use after an ownership change, or if it was shared by mistake)" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50">
            <RefreshCw className="h-3.5 w-3.5" /> Reset
          </button>
        )}
      </div>

      {/* Inline composer — on-file addresses pre-checked + add-your-own. */}
      {emailing && (
        <EmailLinkComposer emails={data.emails} onSend={sendTo} onClose={() => setEmailing(false)} sending={busy} />
      )}
      {!emailing && linkSent && (
        <div className={cn("mt-2 text-center text-[11px] font-medium", linkSent.ok ? "text-emerald-600" : "text-amber-600")}>{linkSent.msg}</div>
      )}
    </div>
  );
}

/** Inline "email the portal link" composer: every on-file address is a
 *  pre-checked chip, and a manual address can be added. */
function EmailLinkComposer({
  emails,
  onSend,
  onClose,
  sending,
}: {
  emails: string[];
  onSend: (emails: string[]) => void;
  onClose: () => void;
  sending: boolean;
}) {
  const [extra, setExtra] = useState<string[]>([]);
  const all = useMemo(() => [...new Set([...emails, ...extra])], [emails, extra]);
  const [checked, setChecked] = useState<Set<string>>(() => new Set(emails));
  const [manual, setManual] = useState("");

  const add = () => {
    const e = manual.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    if (!all.includes(e)) setExtra((x) => [...x, e]);
    setChecked((c) => new Set(c).add(e));
    setManual("");
  };
  const toggle = (e: string) =>
    setChecked((c) => {
      const n = new Set(c);
      if (n.has(e)) n.delete(e);
      else n.add(e);
      return n;
    });
  const recipients = all.filter((e) => checked.has(e));

  return (
    <div className="mt-2 rounded-lg border border-brand-200 bg-brand-50/40 p-2.5">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Email the portal link to</div>
      {all.length === 0 ? (
        <div className="mb-1.5 text-[11px] text-slate-400">No email on file — add one below.</div>
      ) : (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {all.map((e) => (
            <label
              key={e}
              className={cn(
                "inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors",
                checked.has(e) ? "border-brand-300 bg-white text-slate-700" : "border-hairline bg-white/50 text-slate-400",
              )}
            >
              <input type="checkbox" checked={checked.has(e)} onChange={() => toggle(e)} className="h-3 w-3 accent-brand-700" />
              {e}
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add another email…"
          className="flex-1 rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        />
        <button type="button" onClick={add} className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
          Add
        </button>
      </div>
      <div className="mt-2 flex items-center justify-end gap-1.5">
        <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-xs text-slate-500 transition-colors hover:bg-white">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSend(recipients)}
          disabled={recipients.length === 0 || sending}
          className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
        >
          <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : `Send to ${recipients.length || "…"}`}
        </button>
      </div>
    </div>
  );
}

// The secure-link email the office sends when a customer asks for their portal
// access back. Transactional (the customer requested it), so it sends even if
// they've opted out of marketing. Branded to match the portal + templates.
function portalLinkEmail(link: string) {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  return `<div style="max-width:480px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#ffffff;border-radius:14px;overflow:hidden;border:1px solid #e8eaed">
    <div style="text-align:center;padding:26px 24px 20px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
    <div style="padding:30px 34px">
      <h1 style="font-size:20px;color:#16314f;margin:0 0 12px">Your account is ready</h1>
      <p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 22px">Here's your secure link to view your equipment, service history and invoices &mdash; and request service anytime, no password needed.</p>
      <div style="text-align:center"><a href="${link}" style="display:inline-block;background:#c0392b;color:#fff;text-decoration:none;padding:13px 32px;border-radius:9px;font-weight:700;font-size:15px">Open my account</a></div>
      <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:24px 0 0">This link is just for you &mdash; please don't forward it. Questions? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
    </div>
    <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 22px">Sedgwick Heating &amp; Air Conditioning &middot; Eagan, MN</div>
  </div>`;
}
